/**
 * Seed fixtures for the mock data layer.
 *
 * Volumes match or exceed FR-204 minimums.
 * Authored (not randomly generated) for demo reproducibility (NFR-009, ASM-010).
 *
 * This scaffold uses a single file for simplicity. When any module grows,
 * split into per-entity files under this directory and re-export here.
 */

import type {
  Account,
  Asset,
  Attendance,
  AuditEvent,
  Budget,
  Claim,
  Client,
  ComplianceItem,
  Contract,
  CorrespondenceEntry,
  CostCode,
  Currency,
  Dispute,
  DocumentRecord,
  Drawing,
  Employee,
  FxRate,
  IPC,
  Invoice,
  JournalEntry,
  LeaveRequest,
  Lead,
  LegalCase,
  LegalRedline,
  MarketingActivity,
  MaterialRequest,
  MockState,
  Opportunity,
  OrgUnit,
  Payroll,
  PayrollRun,
  Project,
  PurchaseOrder,
  RFP,
  RFQ,
  Role,
  Submittal,
  Supplier,
  SupplierInvoice,
  Tender,
  Timesheet,
  User,
  VariationOrder,
  Warehouse,
  WorkflowLogEntry,
} from '../types';

// ---- Helpers ------------------------------------------------------------

const d = (s: string): string => s;

// ---- Org model ----------------------------------------------------------

const orgUnits: OrgUnit[] = [
  { id: 'president', nameEn: 'President', nameAr: 'الرئيس' },
  { id: 'vpOps', nameEn: 'VP Operations', nameAr: 'نائب الرئيس للعمليات' },
  { id: 'finance', nameEn: 'Finance & Accounting', nameAr: 'المالية والمحاسبة' },
  { id: 'engineering', nameEn: 'Engineering / Technical', nameAr: 'الهندسة / الفني' },
  { id: 'operations', nameEn: 'Operations / Facilities', nameAr: 'العمليات / المرافق' },
  { id: 'contracts', nameEn: 'Contracts & Commercial', nameAr: 'العقود والتجاري' },
  { id: 'marketing', nameEn: 'Marketing & BD', nameAr: 'التسويق وتطوير الأعمال' },
  { id: 'hr', nameEn: 'HR & Administration', nameAr: 'الموارد البشرية والإدارة' },
];

const roles: Role[] = [
  { id: 'r-president', nameEn: 'President', nameAr: 'الرئيس', orgUnitId: 'president' },
  { id: 'r-vpOps', nameEn: 'VP Operations', nameAr: 'نائب الرئيس للعمليات', orgUnitId: 'vpOps' },
  { id: 'r-fin', nameEn: 'Finance Manager', nameAr: 'مدير المالية', orgUnitId: 'finance' },
  { id: 'r-eng', nameEn: 'Engineering Director', nameAr: 'مدير الهندسة', orgUnitId: 'engineering' },
  { id: 'r-ops', nameEn: 'Ops Manager', nameAr: 'مدير العمليات', orgUnitId: 'operations' },
  { id: 'r-ctr', nameEn: 'Contracts Admin', nameAr: 'مسؤول العقود', orgUnitId: 'contracts' },
  { id: 'r-mkt', nameEn: 'Marketing Manager', nameAr: 'مدير التسويق', orgUnitId: 'marketing' },
  { id: 'r-hr', nameEn: 'HR Manager', nameAr: 'مدير الموارد البشرية', orgUnitId: 'hr' },
];

// ---- Currencies & FX ----------------------------------------------------

const currencies: Currency[] = [
  { code: 'SAR', nameEn: 'Saudi Riyal', nameAr: 'ريال سعودي', symbol: 'ر.س', isDefault: true, vatRate: 0.15 },
  { code: 'USD', nameEn: 'US Dollar', nameAr: 'دولار أمريكي', symbol: '$' },
  { code: 'EUR', nameEn: 'Euro', nameAr: 'يورو', symbol: '€' },
  { code: 'AED', nameEn: 'UAE Dirham', nameAr: 'درهم إماراتي', symbol: 'د.إ' },
  { code: 'EGP', nameEn: 'Egyptian Pound', nameAr: 'جنيه مصري', symbol: 'ج.م' },
  { code: 'QAR', nameEn: 'Qatari Riyal', nameAr: 'ريال قطري', symbol: 'ر.ق' },
  { code: 'KWD', nameEn: 'Kuwaiti Dinar', nameAr: 'دينار كويتي', symbol: 'د.ك' },
];

const fxRates: FxRate[] = [
  { date: '2026-01-01', from: 'USD', to: 'SAR', rate: 3.75 },
  { date: '2026-01-01', from: 'EUR', to: 'SAR', rate: 4.1 },
  { date: '2026-01-01', from: 'AED', to: 'SAR', rate: 1.02 },
  { date: '2026-01-01', from: 'EGP', to: 'SAR', rate: 0.077 },
  { date: '2026-01-01', from: 'QAR', to: 'SAR', rate: 1.03 },
  { date: '2026-01-01', from: 'KWD', to: 'SAR', rate: 12.2 },
];

// ---- Cost codes & accounts ---------------------------------------------

const costCodes: CostCode[] = [
  { id: 'cc-01', code: '01-000', nameEn: 'General Conditions', nameAr: 'الشروط العامة' },
  { id: 'cc-02', code: '02-000', nameEn: 'Site Work', nameAr: 'أعمال الموقع' },
  { id: 'cc-03', code: '03-000', nameEn: 'Concrete', nameAr: 'الخرسانة' },
  { id: 'cc-04', code: '32-000', nameEn: 'Landscape — Soft', nameAr: 'زراعة طبيعية' },
  { id: 'cc-05', code: '32-100', nameEn: 'Landscape — Hard', nameAr: 'تشطيبات صلبة' },
  { id: 'cc-06', code: '33-000', nameEn: 'Utilities & Irrigation', nameAr: 'المرافق والري' },
];

const accounts: Account[] = [
  { id: 'a-cash', code: '1000', nameEn: 'Cash', nameAr: 'النقدية', type: 'asset', currency: 'SAR' },
  { id: 'a-ar', code: '1200', nameEn: 'Accounts Receivable', nameAr: 'ذمم مدينة', type: 'asset', currency: 'SAR' },
  { id: 'a-ap', code: '2000', nameEn: 'Accounts Payable', nameAr: 'ذمم دائنة', type: 'liability', currency: 'SAR' },
  { id: 'a-rev', code: '4000', nameEn: 'Project Revenue', nameAr: 'إيرادات المشاريع', type: 'revenue', currency: 'SAR' },
  { id: 'a-cos', code: '5000', nameEn: 'Cost of Services', nameAr: 'تكلفة الخدمات', type: 'expense', currency: 'SAR' },
  { id: 'a-vat', code: '2100', nameEn: 'VAT Payable', nameAr: 'ضريبة القيمة المضافة', type: 'liability', currency: 'SAR' },
];

// ---- Clients & projects ------------------------------------------------

const clients: Client[] = [
  { id: 'c-01', name: 'Royal Commission Riyadh', nameAr: 'الهيئة الملكية بالرياض', segment: 'government', country: 'SA', primaryContact: 'Abdullah Al-Harbi', email: 'abdullah@rcrc.gov.sa' },
  { id: 'c-02', name: 'NEOM Developments', nameAr: 'نيوم للتطوير', segment: 'government', country: 'SA', primaryContact: 'Fatima Al-Qahtani', email: 'f.alqahtani@neom.sa' },
  { id: 'c-03', name: 'Kempinski Al Othman', nameAr: 'كمبينسكي العثمان', segment: 'hospitality', country: 'SA', primaryContact: 'Marco Bellini', email: 'm.bellini@kempinski.com' },
  { id: 'c-04', name: 'Diriyah Gate Development', nameAr: 'هيئة تطوير بوابة الدرعية', segment: 'government', country: 'SA', primaryContact: 'Khalid Al-Sudairy', email: 'k.sudairy@dgda.gov.sa' },
  { id: 'c-05', name: 'Red Sea Global', nameAr: 'البحر الأحمر العالمية', segment: 'hospitality', country: 'SA', primaryContact: 'Rania Al-Ghamdi', email: 'r.ghamdi@redseaglobal.sa' },
  { id: 'c-06', name: 'Qiddiya Investment', nameAr: 'شركة القدية للاستثمار', segment: 'government', country: 'SA', primaryContact: 'Majid Al-Otaibi', email: 'm.otaibi@qiddiya.sa' },
  { id: 'c-07', name: 'Riyadh Front Holdings', nameAr: 'مجموعة واجهة الرياض', segment: 'commercial', country: 'SA', primaryContact: 'Tariq Al-Saud', email: 't.alsaud@rfront.sa' },
  { id: 'c-08', name: 'Emaar KSA', nameAr: 'إعمار السعودية', segment: 'residential', country: 'SA', primaryContact: 'Layla Al-Mansour', email: 'l.mansour@emaar.sa' },
];

const projectPhases = ['inception', 'execution', 'closeOut', 'warranty'] as const;

const projects: Project[] = Array.from({ length: 16 }, (_, i) => {
  const phase = projectPhases[i % 4] ?? 'execution';
  const client = clients[i % clients.length];
  return {
    id: `p-${String(i + 1).padStart(2, '0')}`,
    code: `GA-26-${String(i + 1).padStart(3, '0')}`,
    name: [
      'Diriyah Heritage Landscape',
      'NEOM Line Spine Greening',
      'Kempinski Courtyard Refit',
      'Al-Ahsa Oasis Rehabilitation',
      'King Salman Park Zone 4',
      'Qiddiya Resort Soft Landscape',
      'Red Sea Lagoon Planting',
      'Riyadh Front Phase 2',
      'Medina Waterfront Boulevard',
      'Al-Ula Tantora Gardens',
      'AlKhobar Corniche Renewal',
      'Mahd Forest Nursery',
      'Qassim Date Grove Restoration',
      'Abha Highlands Rewilding',
      'Jeddah Business Park',
      'Taif Rose Valley Corridor',
    ][i] ?? `Landscape Project ${i + 1}`,
    clientId: client?.id ?? 'c-01',
    contractValue: 5_000_000 + i * 1_750_000,
    currency: 'SAR',
    startDate: `2025-${String((i % 9) + 1).padStart(2, '0')}-15`,
    endDate: `2027-${String(((i + 5) % 12) + 1).padStart(2, '0')}-30`,
    phase,
    manager: ['Omar Nasser', 'Hind Al-Otaibi', 'Ziad Al-Saleh', 'Sara Al-Dosari'][i % 4] ?? 'Omar Nasser',
    city: ['Riyadh', 'NEOM', 'AlKhobar', 'Al-Ahsa', 'Medina', 'AlUla', 'Qassim', 'Abha'][i % 8] ?? 'Riyadh',
    country: 'SA',
  };
});

// ---- Contracts, VOs, claims, IPCs --------------------------------------

const contracts: Contract[] = Array.from({ length: 32 }, (_, i) => {
  const proj = projects[i % projects.length]!;
  const isSub = i >= 20;
  return {
    id: `k-${String(i + 1).padStart(3, '0')}`,
    code: `${isSub ? 'SC' : 'CT'}-${String(i + 1).padStart(4, '0')}`,
    projectId: proj.id,
    clientId: proj.clientId,
    type: isSub ? 'subcontract' : 'client',
    ...(isSub ? { subcontractorId: `sup-${(i % 5) + 1}` } : {}),
    originalValue: proj.contractValue * (isSub ? 0.25 : 1),
    currency: proj.currency,
    startDate: proj.startDate,
    endDate: proj.endDate,
    paymentTerms: isSub ? 'Net 45' : 'Net 30 / 10% retention',
    status: (['draft', 'active', 'active', 'active', 'suspended', 'closed'] as const)[i % 6] ?? 'active',
  };
});

const variationOrders: VariationOrder[] = Array.from({ length: 7 }, (_, i) => {
  const ct = contracts[i]!;
  return {
    id: `vo-${String(i + 1).padStart(3, '0')}`,
    contractId: ct.id,
    projectId: ct.projectId,
    code: `VO-${String(i + 1).padStart(3, '0')}`,
    title: ['Additional irrigation zone', 'Shade structures added', 'Soil replacement', 'Lighting upgrade', 'Hard-landscape extension', 'Fountain package', 'Plant species substitution'][i] ?? 'Scope change',
    valueImpact: (i + 1) * 125_000,
    marginImpact: (i + 1) * 9_500,
    // S4-T11 — spread across all 7 statuses (6 happy stages + rejected)
    // so the lifecycle strip demos every position. 7 VOs × 7 statuses = 1 each.
    status: (
      [
        'initiated',
        'techReviewed',
        'commercialReviewed',
        'clientApproved',
        'posted',
        'recomputed',
        'rejected',
      ] as const
    )[i % 7] ?? 'initiated',
    createdAt: `2026-0${(i % 8) + 1}-10`,
  };
});

const claims: Claim[] = Array.from({ length: 4 }, (_, i) => {
  const ct = contracts[i + 3]!;
  return {
    id: `cl-${String(i + 1).padStart(3, '0')}`,
    contractId: ct.id,
    projectId: ct.projectId,
    code: `CLM-${String(i + 1).padStart(3, '0')}`,
    claimedValue: 150_000 + i * 75_000,
    ...(i % 2 === 0 ? { awardedValue: 110_000 + i * 50_000 } : {}),
    status: (['registered', 'evaluated', 'negotiated', 'settled'] as const)[i] ?? 'registered',
    createdAt: `2026-0${i + 2}-05`,
  };
});

const ipcs: IPC[] = Array.from({ length: 10 }, (_, i) => {
  const ct = contracts[i % contracts.length]!;
  const cumulative = (i + 1) * 450_000;
  const retention = cumulative * 0.1;
  const vat = (cumulative - retention) * 0.15;
  return {
    id: `ipc-${String(i + 1).padStart(3, '0')}`,
    contractId: ct.id,
    cycleNumber: (i % 6) + 1,
    cumulativeValue: cumulative,
    retentionAmount: retention,
    vatAmount: vat,
    netAmount: cumulative - retention + vat,
    status: (['draft', 'submitted', 'approved', 'paid'] as const)[i % 4] ?? 'draft',
    issuedDate: `2026-0${(i % 9) + 1}-25`,
  };
});

// ---- Engineering artifacts ----------------------------------------------

const drawings: Drawing[] = Array.from({ length: 14 }, (_, i) => {
  const proj = projects[i % projects.length]!;
  const disciplines = ['architectural', 'structural', 'mep', 'landscape', 'shop', 'asBuilt'] as const;
  const lifecycle = ['shop', 'landscape', 'asBuilt'] as const;
  return {
    id: `dr-${String(i + 1).padStart(3, '0')}`,
    projectId: proj.id,
    title: `Drawing package ${i + 1} — ${proj.code}`,
    discipline: disciplines[i % disciplines.length] ?? 'landscape',
    currentRevision: ['A', 'B', 'C', 'D'][i % 4] ?? 'A',
    status: (['draft', 'submitted', 'underReview', 'approved', 'rejected'] as const)[i % 5] ?? 'draft',
    lifecycleStage: lifecycle[i % 3] ?? 'shop',
    createdAt: `2025-1${(i % 2) + 1}-15`,
  };
});

const submittals: Submittal[] = Array.from({ length: 12 }, (_, i) => ({
  id: `sb-${String(i + 1).padStart(3, '0')}`,
  projectId: (projects[i % projects.length] ?? projects[0]!).id,
  title: `Submittal ${i + 1}`,
  discipline: ['irrigation', 'planting', 'paving', 'lighting'][i % 4] ?? 'planting',
  status: (['draft', 'submitted', 'underReview', 'approved', 'rejected', 'resubmitted'] as const)[i % 6] ?? 'draft',
  submitter: ['Omar Nasser', 'Hind Al-Otaibi', 'Ziad Al-Saleh'][i % 3] ?? 'Omar Nasser',
  ...(i % 2 ? { reviewer: 'Engineering Director' } : {}),
  lastUpdate: `2026-0${(i % 8) + 1}-${String((i % 27) + 1).padStart(2, '0')}`,
}));

const materialRequests: MaterialRequest[] = Array.from({ length: 10 }, (_, i) => ({
  id: `mr-${String(i + 1).padStart(3, '0')}`,
  projectId: (projects[i % projects.length] ?? projects[0]!).id,
  wbsRef: `WBS-${String((i % 20) + 1).padStart(3, '0')}`,
  status: (['draft', 'submitted', 'technicalApproved', 'commercialApproved', 'ordered', 'delivered'] as const)[i % 6] ?? 'draft',
  requestedBy: ['Omar', 'Hind', 'Ziad', 'Sara'][i % 4] ?? 'Omar',
  createdAt: `2026-0${(i % 8) + 1}-${String((i % 27) + 1).padStart(2, '0')}`,
  items: [
    { description: 'Native shrub — 5L', qty: 50 + i * 5, unit: 'ea' },
    { description: 'Topsoil', qty: 10 + i, unit: 'm³' },
  ],
}));

// ---- Procurement --------------------------------------------------------

const suppliers: Supplier[] = [
  { id: 'sup-1', name: 'Desert Nursery Co.', nameAr: 'شركة مشتل الصحراء', category: 'Plants', country: 'SA', email: 'sales@desertnursery.sa' },
  { id: 'sup-2', name: 'Al-Riyadh Paving', nameAr: 'الرياض للتبليط', category: 'Paving', country: 'SA', email: 'info@rpave.sa' },
  { id: 'sup-3', name: 'Gulf Irrigation Systems', nameAr: 'أنظمة الري الخليجية', category: 'Irrigation', country: 'AE', email: 'sales@gulfirr.ae' },
  { id: 'sup-4', name: 'Mediterranean Lighting', nameAr: 'إضاءة البحر المتوسط', category: 'Lighting', country: 'EG', email: 'hello@medlight.com' },
  { id: 'sup-5', name: 'KSA Fountain Works', nameAr: 'نوافير المملكة', category: 'Water features', country: 'SA', email: 'info@fountains.sa' },
  { id: 'sup-6', name: 'Qatar Stone Traders', nameAr: 'تجار الأحجار القطرية', category: 'Stone', country: 'QA', email: 'orders@qstone.qa' },
];

const rfqs: RFQ[] = Array.from({ length: 5 }, (_, i) => ({
  id: `rfq-${String(i + 1).padStart(3, '0')}`,
  projectId: (projects[i] ?? projects[0]!).id,
  costCodeId: (costCodes[i % costCodes.length] ?? costCodes[0]!).id,
  title: `RFQ — package ${i + 1}`,
  status: (['draft', 'sent', 'repliesReceived', 'awarded'] as const)[i % 4] ?? 'draft',
  issuedDate: `2026-0${(i % 8) + 1}-12`,
}));

const rfps: RFP[] = Array.from({ length: 3 }, (_, i) => ({
  id: `rfp-${String(i + 1).padStart(3, '0')}`,
  projectId: (projects[i + 3] ?? projects[0]!).id,
  costCodeId: (costCodes[(i + 1) % costCodes.length] ?? costCodes[0]!).id,
  title: `RFP — consulting package ${i + 1}`,
  status: (['draft', 'sent', 'repliesReceived'] as const)[i % 3] ?? 'draft',
  issuedDate: `2026-0${(i % 6) + 2}-20`,
}));

const purchaseOrders: PurchaseOrder[] = Array.from({ length: 8 }, (_, i) => ({
  id: `po-${String(i + 1).padStart(3, '0')}`,
  code: `PO-2026-${String(i + 1).padStart(4, '0')}`,
  projectId: (projects[i % projects.length] ?? projects[0]!).id,
  costCodeId: (costCodes[i % costCodes.length] ?? costCodes[0]!).id,
  supplierId: (suppliers[i % suppliers.length] ?? suppliers[0]!).id,
  totalValue: 50_000 + i * 17_500,
  currency: 'SAR',
  status: (['draft', 'approved', 'issued', 'partialReceipt', 'closed'] as const)[i % 5] ?? 'draft',
  issuedDate: `2026-0${(i % 8) + 1}-14`,
}));

// ---- HR -----------------------------------------------------------------

const employeeUnits = ['president', 'vpOps', 'finance', 'engineering', 'operations', 'contracts', 'marketing', 'hr'] as const;

const employees: Employee[] = Array.from({ length: 54 }, (_, i) => {
  const unit = employeeUnits[i % employeeUnits.length] ?? 'operations';
  return {
    id: `e-${String(i + 1).padStart(3, '0')}`,
    employeeNumber: `GA-${String(i + 1).padStart(4, '0')}`,
    fullName: [
      'Omar Nasser',
      'Hind Al-Otaibi',
      'Ziad Al-Saleh',
      'Sara Al-Dosari',
      'Ahmed Al-Ghamdi',
      'Layla Al-Sabah',
      'Mohammed Al-Rashid',
      'Noura Al-Harbi',
      'Khalid Al-Qahtani',
      'Maha Al-Zahrani',
    ][i % 10] + ` (${i + 1})`,
    orgUnitId: unit,
    roleTitle: ['Engineer', 'Supervisor', 'Manager', 'Coordinator', 'Analyst'][i % 5] ?? 'Engineer',
    email: `employee${i + 1}@gardenart.sa`,
    joinDate: `202${(i % 4) + 1}-0${(i % 9) + 1}-15`,
    iqamaExpiry: `2026-${String(((i + 1) % 12) + 1).padStart(2, '0')}-15`,
    ...(i % 3 === 0 ? { passportExpiry: `2027-${String((i % 12) + 1).padStart(2, '0')}-01` } : {}),
    ...(i % 5 === 0 ? { licenseExpiry: `2026-${String((i % 12) + 1).padStart(2, '0')}-20` } : {}),
    status: (['active', 'active', 'active', 'onLeave', 'terminated'] as const)[i % 5] ?? 'active',
  };
});

const attendance: Attendance[] = [];
for (let emp = 0; emp < Math.min(employees.length, 20); emp++) {
  for (let day = 0; day < 180; day++) {
    attendance.push({
      id: `at-${emp}-${day}`,
      employeeId: employees[emp]!.id,
      date: new Date(2025, 10, 1 + day).toISOString().slice(0, 10),
      status: day % 7 === 5 || day % 7 === 6 ? 'holiday' : day % 13 === 0 ? 'absent' : 'present',
      hours: day % 7 === 5 || day % 7 === 6 ? 0 : 8,
    });
  }
}

const timesheets: Timesheet[] = Array.from({ length: 60 }, (_, i) => ({
  id: `ts-${String(i + 1).padStart(4, '0')}`,
  employeeId: employees[i % 20]!.id,
  projectId: projects[i % projects.length]!.id,
  date: `2026-0${(i % 6) + 1}-${String((i % 27) + 1).padStart(2, '0')}`,
  hours: 4 + (i % 5),
  approved: i % 3 !== 0,
}));

const leaveRequests: LeaveRequest[] = Array.from({ length: 12 }, (_, i) => ({
  id: `lv-${String(i + 1).padStart(3, '0')}`,
  employeeId: employees[i]!.id,
  type: (['annual', 'sick', 'unpaid', 'hajj'] as const)[i % 4] ?? 'annual',
  startDate: `2026-0${(i % 8) + 1}-05`,
  endDate: `2026-0${(i % 8) + 1}-${String(6 + (i % 5)).padStart(2, '0')}`,
  status: (['pending', 'approved', 'rejected'] as const)[i % 3] ?? 'pending',
}));

const payroll: Payroll[] = [];
for (let month = 0; month < 6; month++) {
  for (let emp = 0; emp < Math.min(employees.length, 20); emp++) {
    payroll.push({
      id: `pr-${month}-${emp}`,
      employeeId: employees[emp]!.id,
      period: `2026-0${month + 1}`,
      basic: 8_000 + (emp % 5) * 1_500,
      allowances: 1_500,
      deductions: 400,
      net: 9_100 + (emp % 5) * 1_500,
      currency: 'SAR',
      status: month < 5 ? 'submitted' : 'previewed',
    });
  }
}

// ---- Leads, tenders, legal ---------------------------------------------

const leadStages = ['new', 'qualified', 'proposal', 'negotiation', 'won', 'lost'] as const;

const leadTitles = [
  'Riyadh palace gardens — annual maintenance',
  'Kempinski rooftop garden',
  'NEOM Spine Phase 4 landscape',
  'Diriyah Square fountains',
  'King Abdullah Park expansion',
  'Red Sea Resort planting package',
  'Jeddah Corniche refit',
  'AlUla heritage gardens',
  'Riyadh Metro station landscape',
  'Qiddiya Grand Prix greening',
  'Al-Khobar Corniche nursery',
  'Taif Rose Valley nursery refit',
  'Hail citrus-belt landscape',
  'Sindalah island soft-landscape',
  'Yanbu waterfront planting',
];

const leads: Lead[] = Array.from({ length: 25 }, (_, i) => ({
  id: `ld-${String(i + 1).padStart(3, '0')}`,
  ...(i % 2 === 0 ? { clientId: clients[i % clients.length]!.id } : {}),
  title: leadTitles[i % leadTitles.length] ?? `Opportunity ${i + 1}`,
  value: 500_000 + i * 250_000,
  currency: 'SAR',
  stage: leadStages[i % leadStages.length] ?? 'new',
  probability: [0.1, 0.3, 0.5, 0.7, 1, 0][i % 6] ?? 0.3,
  owner: ['Noura', 'Yasser', 'Hala', 'Hatem', 'Salma'][i % 5] ?? 'Noura',
  createdAt: `2026-0${(i % 8) + 1}-${String((i % 27) + 1).padStart(2, '0')}`,
}));

const tenderStages = ['identified', 'bidDecision', 'preparing', 'submitted', 'awarded', 'lost'] as const;

const tenders: Tender[] = Array.from({ length: 10 }, (_, i) => ({
  id: `tn-${String(i + 1).padStart(3, '0')}`,
  title: [
    'MOMRAH — Riyadh boulevards landscape framework',
    'NEOM — Spine Phase 5 greening',
    'Red Sea — Shura Island soft landscape',
    'Diriyah Gate — heritage plaza fountains',
    'Qiddiya — entertainment-district planting',
    'Riyadh Season — temporary festival landscape',
    'RCRC — Wadi Hanifah rehabilitation',
    'King Salman Park — Zone 6 planting',
    'Jeddah Corniche — phase 2 extension',
    'AlUla — Sharaan resort soft landscape',
  ][i] ?? `Tender ${i + 1}`,
  ...(i % 2 === 0 ? { clientId: clients[i % clients.length]!.id } : {}),
  submissionDate: `2026-0${(i % 8) + 2}-${String(10 + (i % 18)).padStart(2, '0')}`,
  value: 5_000_000 + i * 2_500_000,
  currency: 'SAR',
  stage: tenderStages[i % tenderStages.length] ?? 'identified',
}));

// ---- Sprint 3 additions: Opportunities, Marketing Activities ------------

const opportunityStages = ['discovery', 'scoping', 'proposalDraft', 'negotiation', 'won', 'lost'] as const;

const opportunities: Opportunity[] = Array.from({ length: 20 }, (_, i) => {
  const leadRef = leads[i % leads.length]!;
  return {
    id: `opp-${String(i + 1).padStart(3, '0')}`,
    code: `OPP-2026-${String(i + 1).padStart(4, '0')}`,
    title: [
      'Master landscape — Phase 1',
      'Irrigation master plan',
      'Rooftop garden package',
      'Annual maintenance framework',
      'Public-realm planting',
      'Heritage fountain replica',
      'Hard-landscape paving upgrade',
      'Urban tree-canopy expansion',
      'Desert-native wildflower corridor',
      'Ceremonial garden design-build',
    ][i % 10] ?? `Opportunity scope ${i + 1}`,
    leadId: leadRef.id,
    ...(leadRef.clientId ? { clientId: leadRef.clientId } : {}),
    estimatedValue: 750_000 + i * 325_000,
    currency: 'SAR',
    probability: [0.1, 0.25, 0.4, 0.6, 0.85, 1, 0][i % 7] ?? 0.25,
    stage: opportunityStages[i % opportunityStages.length] ?? 'discovery',
    expectedCloseDate: `2026-${String(((i + 6) % 12) + 1).padStart(2, '0')}-${String(10 + (i % 18)).padStart(2, '0')}`,
    owner: ['Noura', 'Yasser', 'Hala', 'Hatem'][i % 4] ?? 'Noura',
  };
});

const activityTypes: MarketingActivity['type'][] = [
  'call',
  'meeting',
  'email',
  'event',
  'campaign',
  'demo',
  'site-visit',
];

const activityOutcomes: MarketingActivity['outcome'][] = ['pending', 'positive', 'neutral', 'negative'];

const activitySubjects = [
  'Intro call — project scoping',
  'Client-site walk through',
  'Bid clarification meeting',
  'Trade-show booth — Saudi Horticulture',
  'Quarterly newsletter campaign',
  'Design presentation demo',
  'Technical walk-down with client',
  'Follow-up on proposal feedback',
  'Price-alignment negotiation call',
  'Due-diligence meeting with legal',
  'Stakeholder alignment workshop',
  'Mock-up planting visit',
  'Contract handover meeting',
  'Competitor intelligence brief',
  'RFI clarification call',
];

const marketingActivities: MarketingActivity[] = Array.from({ length: 32 }, (_, i) => {
  const linkMode = i % 3; // 0 = lead, 1 = client, 2 = tender
  const leadRef = leads[i % leads.length]!;
  const clientRef = clients[i % clients.length]!;
  const tenderRef = tenders[i % tenders.length]!;
  return {
    id: `mact-${String(i + 1).padStart(3, '0')}`,
    type: activityTypes[i % activityTypes.length] ?? 'call',
    subject: activitySubjects[i % activitySubjects.length] ?? `Activity ${i + 1}`,
    date: `2026-0${(i % 8) + 1}-${String((i % 27) + 1).padStart(2, '0')}`,
    owner: ['Noura', 'Yasser', 'Hala', 'Hatem', 'Salma'][i % 5] ?? 'Noura',
    outcome: activityOutcomes[i % activityOutcomes.length] ?? 'pending',
    ...(linkMode === 0 ? { leadId: leadRef.id } : {}),
    ...(linkMode === 1 ? { clientId: clientRef.id } : {}),
    ...(linkMode === 2 ? { tenderId: tenderRef.id } : {}),
    ...(i % 4 === 0 ? { notes: 'Client confirmed budget range — proceed to scoping.' } : {}),
  };
});

// ---- Sprint 3 additions: Legal redlines, compliance, disputes -----------

const redlineStages: LegalRedline['stage'][] = [
  'drafting',
  'externalReview',
  'internalReview',
  'approved',
  'signed',
];

const legalRedlines: LegalRedline[] = Array.from({ length: 14 }, (_, i) => {
  const contract = contracts[i % contracts.length]!;
  return {
    id: `rdl-${String(i + 1).padStart(3, '0')}`,
    code: `RDL-${String(i + 1).padStart(3, '0')}`,
    contractId: contract.id,
    title: [
      'Liquidated-damages cap re-draft',
      'Indemnity clause expansion',
      'Retention percentage negotiation',
      'Termination-for-convenience clause',
      'IP ownership clarification',
      'Subcontract flow-down amendment',
      'VAT treatment on milestone pay',
      'Performance-bond wording change',
      'Notice-of-claim window extension',
      'Force-majeure list update',
      'Insurance thresholds increase',
      'Governing-law clause revision',
      'Delay-damages schedule attachment',
      'Novation to affiliate entity',
    ][i] ?? `Redline package ${i + 1}`,
    stage: redlineStages[i % redlineStages.length] ?? 'drafting',
    owner: ['Lina', 'Adel', 'Rasha'][i % 3] ?? 'Lina',
    lastUpdate: `2026-0${(i % 8) + 1}-${String(5 + (i % 20)).padStart(2, '0')}`,
    openComments: (i * 3) % 12,
  };
});

const complianceCategories: ComplianceItem['category'][] = [
  'licence',
  'certification',
  'regulatoryFiling',
  'insurance',
  'hseCertificate',
];

function pickComplianceStatus(
  expiryDate: string,
  today = '2026-04-23',
): ComplianceItem['status'] {
  const diffDays =
    (new Date(expiryDate).getTime() - new Date(today).getTime()) / (1000 * 60 * 60 * 24);
  if (diffDays < 0) return 'expired';
  if (diffDays < 60) return 'expiringSoon';
  return 'valid';
}

const complianceItems: ComplianceItem[] = Array.from({ length: 16 }, (_, i) => {
  const expiryDate = `2026-${String(((i + 1) % 12) + 1).padStart(2, '0')}-${String(5 + (i % 22)).padStart(2, '0')}`;
  return {
    id: `cmp-${String(i + 1).padStart(3, '0')}`,
    code: `CMP-${String(i + 1).padStart(3, '0')}`,
    title: [
      'Commercial Registration — Riyadh HQ',
      'Chamber of Commerce membership',
      'MOMRAH contractor classification',
      'ISO 9001 quality-management',
      'ISO 14001 environmental-management',
      'ISO 45001 occupational-safety',
      'Municipal licence — Al-Ahsa branch',
      'Saudi Council of Engineers — firm registration',
      'Public liability insurance',
      'Professional indemnity cover',
      'Workers compensation — GOSI',
      'ZATCA VAT registration',
      'Horticultural import licence',
      'Civil defence HSE certificate',
      'NEOM vendor pre-qualification',
      'Diriyah Gate authorised-vendor list',
    ][i] ?? `Compliance item ${i + 1}`,
    category: complianceCategories[i % complianceCategories.length] ?? 'licence',
    issuer: [
      'Ministry of Commerce',
      'Riyadh Chamber',
      'MOMRAH',
      'ISO',
      'SCE',
      'Saudi Central Bank',
      'ZATCA',
    ][i % 7] ?? 'Ministry of Commerce',
    expiryDate,
    status: i % 11 === 0 ? 'renewed' : pickComplianceStatus(expiryDate),
    owner: ['Lina', 'Adel', 'Rasha'][i % 3] ?? 'Lina',
  };
});

const disputeStages: Dispute['stage'][] = [
  'notified',
  'mediation',
  'arbitration',
  'litigation',
  'settled',
  'closed',
];

const disputes: Dispute[] = Array.from({ length: 8 }, (_, i) => {
  const contract = contracts[(i * 3) % contracts.length]!;
  const claim = claims[i % claims.length];
  const vo = variationOrders[(i + 1) % variationOrders.length];
  return {
    id: `dsp-${String(i + 1).padStart(3, '0')}`,
    code: `DSP-${String(i + 1).padStart(3, '0')}`,
    title: [
      'Access-delay liquidated damages dispute',
      'Scope-creep compensation claim',
      'Material-substitution cost recovery',
      'Liquidated damages cap challenge',
      'Retention release dispute',
      'Sub-contractor back-charges',
      'Design-change notification window',
      'Final-account reconciliation',
    ][i] ?? `Dispute case ${i + 1}`,
    counterparty: [
      'Kempinski Al Othman',
      'NEOM Developments',
      'Diriyah Gate Development',
      'Royal Commission Riyadh',
      'Red Sea Global',
      'Emaar KSA',
      'Qiddiya Investment',
      'Riyadh Front Holdings',
    ][i] ?? 'Counterparty',
    contractId: contract.id,
    ...(i % 2 === 0 && claim ? { relatedClaimId: claim.id } : {}),
    ...(i % 3 === 0 && vo ? { relatedVoId: vo.id } : {}),
    stage: disputeStages[i % disputeStages.length] ?? 'notified',
    exposureValue: 180_000 + i * 95_000,
    currency: 'SAR',
    openedDate: `2026-0${(i % 6) + 2}-${String(8 + (i % 16)).padStart(2, '0')}`,
    owner: ['Lina', 'Adel', 'Rasha'][i % 3] ?? 'Lina',
  };
});

const legalCases: LegalCase[] = Array.from({ length: 4 }, (_, i) => ({
  id: `lg-${String(i + 1).padStart(3, '0')}`,
  title: ['Kempinski redline', 'NEOM compliance review', 'Diriyah dispute', 'Qiddiya NDA'][i] ?? `Case ${i + 1}`,
  ...(i < clients.length ? { clientId: clients[i]!.id } : {}),
  type: (['redline', 'compliance', 'dispute', 'redline'] as const)[i] ?? 'redline',
  stage: (['opened', 'inReview', 'negotiation', 'resolved'] as const)[i] ?? 'opened',
  createdAt: `2026-0${i + 1}-10`,
}));

// ---- Documents, audit, misc --------------------------------------------

const documents: DocumentRecord[] = Array.from({ length: 110 }, (_, i) => {
  const kind = (['contract', 'drawing', 'submittal', 'invoice', 'manual', 'letter', 'other'] as const)[i % 7] ?? 'other';
  return {
    id: `doc-${String(i + 1).padStart(4, '0')}`,
    title: `Document ${i + 1} — ${kind}`,
    type: kind,
    version: `v${(i % 3) + 1}`,
    superseded: i % 11 === 0,
    ...(i % 2 === 0 ? { projectId: projects[i % projects.length]!.id } : {}),
    ...(i % 3 === 0 ? { contractId: contracts[i % contracts.length]!.id } : {}),
    ...(i % 5 === 0 ? { employeeId: employees[i % employees.length]!.id } : {}),
    uploadedBy: ['Omar', 'Hind', 'Ziad', 'Sara'][i % 4] ?? 'Omar',
    uploadedAt: `2026-0${(i % 8) + 1}-${String((i % 27) + 1).padStart(2, '0')}`,
    sizeKb: 120 + (i % 500),
  };
});

// S4-T14 — Two demo supersedes chains so the version history popover has
// something interesting to show on first visit. Pinned to the first project
// + first contract so they show up in the most-trafficked detail tabs.
const firstProject = projects[0]!;
const firstContract = contracts[0]!;
const drawingChain: DocumentRecord[] = [
  {
    id: 'doc-chain-d-v1',
    title: 'Site grading drawing — Zone A',
    type: 'drawing',
    version: 'v1',
    superseded: true,
    projectId: firstProject.id,
    uploadedBy: 'Omar Nasser',
    uploadedAt: '2026-02-04',
    sizeKb: 1840,
  },
  {
    id: 'doc-chain-d-v2',
    title: 'Site grading drawing — Zone A',
    type: 'drawing',
    version: 'v2',
    superseded: true,
    supersedesId: 'doc-chain-d-v1',
    projectId: firstProject.id,
    uploadedBy: 'Hind Al-Otaibi',
    uploadedAt: '2026-03-12',
    sizeKb: 1920,
  },
  {
    id: 'doc-chain-d-v3',
    title: 'Site grading drawing — Zone A',
    type: 'drawing',
    version: 'v3',
    superseded: false,
    supersedesId: 'doc-chain-d-v2',
    projectId: firstProject.id,
    uploadedBy: 'Hind Al-Otaibi',
    uploadedAt: '2026-04-21',
    sizeKb: 2048,
  },
];
const contractChain: DocumentRecord[] = [
  {
    id: 'doc-chain-c-v1',
    title: 'Master service agreement',
    type: 'contract',
    version: 'v1',
    superseded: true,
    contractId: firstContract.id,
    uploadedBy: 'Sara Al-Dosari',
    uploadedAt: '2025-12-15',
    sizeKb: 540,
  },
  {
    id: 'doc-chain-c-v2',
    title: 'Master service agreement',
    type: 'contract',
    version: 'v2 (countersigned)',
    superseded: false,
    supersedesId: 'doc-chain-c-v1',
    contractId: firstContract.id,
    uploadedBy: 'Sara Al-Dosari',
    uploadedAt: '2026-01-09',
    sizeKb: 612,
  },
];
documents.push(...drawingChain, ...contractChain);

const auditEntityTypes = [
  'contract',
  'vo',
  'ipc',
  'employee',
  'project',
  'lead',
  'tender',
  'opportunity',
  'redline',
  'dispute',
  'compliance',
  'client',
] as const;

const auditUsers = [
  'Faisal Al-Mansour',
  'Huda Al-Shehri',
  'Sami Al-Dabbagh',
  'Reema Al-Khaldi',
  'Ibrahim Al-Enezi',
  'Lina Al-Malki',
  'Yasser Al-Rashidi',
  'Noor Al-Subaie',
] as const;

const auditNotes = [
  'Approved via mobile app',
  'Auto-generated from workflow',
  'Reviewed in weekly standup',
  'Counter-signed by legal',
  'Flagged for finance review',
  'Pending HR acknowledgement',
] as const;

/**
 * Audit events — anchored to REAL entity IDs so the activity timeline on
 * project / contract / etc. detail drawers actually populates. Earlier
 * versions used placeholder `e-N` IDs that never matched anything.
 *
 * Strategy: each event picks an entity type, then a real id from the
 * matching collection (cycling i % length). For types that don't have a
 * fixture collection yet (lead/tender/etc.), the entityId stays generic
 * — the project & contract drawers just won't show those.
 */
const auditEntityIdPool: Record<(typeof auditEntityTypes)[number], string[]> = {
  contract: [], // populated below once `contracts` is defined
  vo: [],
  ipc: [],
  employee: [],
  project: projects.map((p) => p.id),
  lead: [],
  tender: [],
  opportunity: [],
  redline: [],
  dispute: [],
  compliance: [],
  client: clients.map((c) => c.id),
};
const auditEvents: AuditEvent[] = Array.from({ length: 56 }, (_, i) => {
  const entity = auditEntityTypes[i % auditEntityTypes.length] ?? 'project';
  const pool = auditEntityIdPool[entity];
  // Fall back to project IDs (always populated) when the pool is empty —
  // makes sure every audit row attaches to *something*.
  const ids = pool.length > 0 ? pool : auditEntityIdPool.project;
  const entityId = ids[i % ids.length] ?? 'p-01';
  return {
    id: `au-${String(i + 1).padStart(4, '0')}`,
    entity,
    entityId,
    action:
      (['created', 'updated', 'approved', 'rejected', 'deleted'] as const)[i % 5] ??
      'created',
    user: auditUsers[i % auditUsers.length] ?? 'Omar',
    at: `2026-0${(i % 8) + 1}-${String((i % 27) + 1).padStart(2, '0')}T${String(9 + (i % 8)).padStart(2, '0')}:${String((i * 7) % 60).padStart(2, '0')}:00Z`,
    ...(i % 2 === 0 ? { note: auditNotes[i % auditNotes.length] } : {}),
  };
});

const warehouses: Warehouse[] = [
  { id: 'wh-1', code: 'RYD-MAIN', name: 'Riyadh Main Yard', location: 'Riyadh' },
  { id: 'wh-2', code: 'JED-01', name: 'Jeddah Coastal Store', location: 'Jeddah' },
  { id: 'wh-3', code: 'NEOM-FLD', name: 'NEOM Field Depot', location: 'NEOM' },
];

const assets: Asset[] = Array.from({ length: 8 }, (_, i) => ({
  id: `as-${String(i + 1).padStart(3, '0')}`,
  code: `EQP-${String(i + 1).padStart(4, '0')}`,
  name: ['Excavator JCB', 'Crew truck', 'Mini-loader', 'Water tanker', 'Scissor lift', 'Skid-steer', 'Compactor', 'Mulch chipper'][i] ?? 'Equipment',
  category: (['equipment', 'vehicle', 'tool'] as const)[i % 3] ?? 'equipment',
  location: warehouses[i % warehouses.length]!.location,
  status: (['active', 'maintenance', 'active', 'retired'] as const)[i % 4] ?? 'active',
  lastServiceDate: `2026-0${(i % 6) + 1}-10`,
  nextServiceDue: `2026-${String(((i + 5) % 12) + 1).padStart(2, '0')}-10`,
}));

const budgets: Budget[] = projects.flatMap((proj, pi) =>
  costCodes.map((cc, ci) => ({
    id: `bd-${pi}-${ci}`,
    projectId: proj.id,
    costCodeId: cc.id,
    budgeted: 200_000 + pi * 20_000 + ci * 15_000,
    committed: 120_000 + pi * 10_000 + ci * 8_000,
    actual: 90_000 + pi * 9_000 + ci * 5_000,
    currency: 'SAR',
  })),
);

const invoices: Invoice[] = Array.from({ length: 12 }, (_, i) => ({
  id: `inv-${String(i + 1).padStart(3, '0')}`,
  contractId: contracts[i % contracts.length]!.id,
  amount: 180_000 + i * 45_000,
  currency: 'SAR',
  dueDate: `2026-0${(i % 8) + 1}-${String(((i * 3) % 27) + 1).padStart(2, '0')}`,
  status: (['open', 'paid', 'overdue'] as const)[i % 3] ?? 'open',
}));

const supplierInvoices: SupplierInvoice[] = Array.from({ length: 10 }, (_, i) => ({
  id: `spi-${String(i + 1).padStart(3, '0')}`,
  poId: purchaseOrders[i % purchaseOrders.length]!.id,
  supplierId: suppliers[i % suppliers.length]!.id,
  amount: 40_000 + i * 12_000,
  currency: 'SAR',
  dueDate: `2026-0${(i % 8) + 1}-${String(((i * 2) % 27) + 1).padStart(2, '0')}`,
  status: (['pending', 'approved', 'paid'] as const)[i % 3] ?? 'pending',
}));

// ---- Users (matches 9 personas — 8 role-scoped + 1 bilingual viewer) ----

const users: User[] = [
  { id: 'u-president', fullName: 'Faisal Al-Mansour', fullNameAr: 'فيصل المنصور', email: 'faisal@gardenart.sa', roleId: 'r-president', orgUnitId: 'president', active: true },
  { id: 'u-vpOps', fullName: 'Huda Al-Shehri', fullNameAr: 'هدى الشهري', email: 'huda@gardenart.sa', roleId: 'r-vpOps', orgUnitId: 'vpOps', active: true },
  { id: 'u-fin', fullName: 'Sami Al-Dabbagh', fullNameAr: 'سامي الدباغ', email: 'sami@gardenart.sa', roleId: 'r-fin', orgUnitId: 'finance', active: true },
  { id: 'u-eng', fullName: 'Reema Al-Khaldi', fullNameAr: 'ريما الخالدي', email: 'reema@gardenart.sa', roleId: 'r-eng', orgUnitId: 'engineering', active: true },
  { id: 'u-ops', fullName: 'Ibrahim Al-Enezi', fullNameAr: 'إبراهيم العنزي', email: 'ibrahim@gardenart.sa', roleId: 'r-ops', orgUnitId: 'operations', active: true },
  { id: 'u-ctr', fullName: 'Lina Al-Malki', fullNameAr: 'لينا المالكي', email: 'lina@gardenart.sa', roleId: 'r-ctr', orgUnitId: 'contracts', active: true },
  { id: 'u-mkt', fullName: 'Yasser Al-Rashidi', fullNameAr: 'ياسر الراشدي', email: 'yasser@gardenart.sa', roleId: 'r-mkt', orgUnitId: 'marketing', active: true },
  { id: 'u-hr', fullName: 'Noor Al-Subaie', fullNameAr: 'نور السبيعي', email: 'noor@gardenart.sa', roleId: 'r-hr', orgUnitId: 'hr', active: true },
];

// ---- Light journal entries (for GL preview) -----------------------------

const journalEntries: JournalEntry[] = Array.from({ length: 30 }, (_, i) => ({
  id: `je-${String(i + 1).padStart(4, '0')}`,
  date: `2026-0${(i % 8) + 1}-${String((i % 27) + 1).padStart(2, '0')}`,
  accountId: (accounts[i % accounts.length] ?? accounts[0]!).id,
  debit: i % 2 === 0 ? 25_000 + i * 1000 : 0,
  credit: i % 2 === 0 ? 0 : 25_000 + i * 1000,
  currency: 'SAR',
  ...(i % 3 === 0 ? { projectId: projects[i % projects.length]!.id } : {}),
  description: d(`Journal entry ${i + 1}`),
}));

// ---- Commercial correspondence (S2-T24 / FR-085) ------------------------

const correspondenceCategories: CorrespondenceEntry['category'][] = [
  'letter',
  'rfi',
  'notice',
  'objection',
];
const correspondenceStatuses: CorrespondenceEntry['status'][] = [
  'draft',
  'sent',
  'received',
  'acknowledged',
  'closed',
];

const correspondence: CorrespondenceEntry[] = Array.from({ length: 12 }, (_, i) => {
  const contract = contracts[i % contracts.length]!;
  const contractLinked = i % 3 !== 2; // two-thirds are contract-scoped
  const category =
    correspondenceCategories[i % correspondenceCategories.length] ?? 'letter';
  const status = correspondenceStatuses[i % correspondenceStatuses.length] ?? 'sent';
  return {
    id: `corr-${String(i + 1).padStart(3, '0')}`,
    code: `COR-${String(i + 1).padStart(3, '0')}`,
    category,
    subject:
      [
        'Extension of time — rainy season',
        'Request for information — planting spec',
        'Notice of pending claim — access delay',
        'Objection — material substitution',
        'Progress report transmittal',
        'Clarification on VO-003 scope',
        'Acknowledgement — IPC-005',
        'Notice — access restriction',
        'Formal submission — as-built package',
        'RFI — irrigation controller brand',
        'Objection — liquidated damages calculation',
        'Handover letter — Phase 1',
      ][i] ?? 'Commercial correspondence',
    direction: i % 2 === 0 ? 'outbound' : 'inbound',
    counterparty:
      i % 2 === 0
        ? clients[i % clients.length]?.name ?? '—'
        : suppliers[i % suppliers.length]?.name ?? '—',
    status,
    issuedDate: `2026-0${(i % 8) + 1}-${String((i % 27) + 1).padStart(2, '0')}`,
    scope: contractLinked ? 'contract' : 'generic',
    ...(contractLinked ? { contractId: contract.id } : {}),
    ...(i % 4 === 0 && variationOrders[i % variationOrders.length]
      ? { relatedVoId: variationOrders[i % variationOrders.length]!.id }
      : {}),
    ...(i % 5 === 0 && claims[i % claims.length]
      ? { relatedClaimId: claims[i % claims.length]!.id }
      : {}),
  };
});

// ---- Workflow run log (S2-T31..T35 / FR-148, FR-149) --------------------

// Seed empty — entries are appended when a workflow wizard is completed.
const workflowLog: WorkflowLogEntry[] = [];

// ---- Payroll runs (S3-W01 / FR-114) -------------------------------------

// Seed empty — Attendance → Payroll wizard appends a new run on completion.
const payrollRuns: PayrollRun[] = [];

// ---- Assembled state ----------------------------------------------------

export const initialMockState: MockState = {
  orgUnits,
  roles,
  users,
  currencies,
  fxRates,
  costCodes,
  accounts,
  journalEntries,
  projects,
  clients,
  contracts,
  variationOrders,
  claims,
  correspondence,
  ipcs,
  drawings,
  submittals,
  materialRequests,
  suppliers,
  rfqs,
  rfps,
  purchaseOrders,
  employees,
  attendance,
  timesheets,
  leaveRequests,
  payroll,
  leads,
  tenders,
  legalCases,
  marketingActivities,
  opportunities,
  legalRedlines,
  complianceItems,
  disputes,
  documents,
  auditEvents,
  warehouses,
  assets,
  budgets,
  invoices,
  supplierInvoices,
  payrollRuns,
  workflowLog,
};

/** Summary for README/demo dashboards. */
export const fixtureVolumeSummary = {
  projects: projects.length,
  contracts: contracts.length,
  employees: employees.length,
  leads: leads.length,
  documents: documents.length,
  suppliers: suppliers.length,
  drawings: drawings.length,
  materialRequests: materialRequests.length,
  purchaseOrders: purchaseOrders.length,
  variationOrders: variationOrders.length,
  claims: claims.length,
  correspondence: correspondence.length,
  tenders: tenders.length,
  clients: clients.length,
  opportunities: opportunities.length,
  marketingActivities: marketingActivities.length,
  legalRedlines: legalRedlines.length,
  complianceItems: complianceItems.length,
  disputes: disputes.length,
  auditEvents: auditEvents.length,
  payrollRecords: payroll.length,
  attendanceRecords: attendance.length,
};
